import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { sectionRenderer } from '@theme/section-renderer';

const CART_CONTENT_RENDERED_EVENT = 'yomia:cart-content-rendered';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {boolean} */
  #pendingAutoOpen = false;

  /** @type {HTMLDialogElement | null} */
  #pendingSourceModal = null;

  /** @type {boolean} */
  #contentReady = true;

  /** @type {Promise<void>} */
  #contentReadyPromise = Promise.resolve();

  /** @type {(() => void) | null} */
  #resolveContentReady = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.addEventListener(CART_CONTENT_RENDERED_EVENT, this.#handleCartContentRendered);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    document.addEventListener('click', this.#handleCartTriggerClick, {
      capture: true,
      signal: this.#abortController.signal,
    });

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.removeEventListener(CART_CONTENT_RENDERED_EVENT, this.#handleCartContentRendered);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    if (event.action === 'add') this.#markContentPending();

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    this.#pendingAutoOpen = shouldAutoOpen;
    this.#pendingSourceModal = sourceModal;

    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) {
          this.#markContentReady();
          this.#pendingAutoOpen = false;
          this.#pendingSourceModal = null;
        }
      })
      .catch((error) => {
        this.#markContentReady();
        this.#pendingAutoOpen = false;
        this.#pendingSourceModal = null;
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  /**
   * Opens only after this drawer's cart section has finished morphing from the
   * same confirmed /cart/add response.
   */
  #handleCartContentRendered = async () => {
    await customElements.whenDefined('cart-items-component');
    if (window.matchMedia('(max-width: 989px)').matches) {
      await new Promise((resolve) => requestAnimationFrame(resolve));
    } else {
      await Promise.resolve();
    }
    this.#markContentReady();

    const openAndSettle = () => {
      if (this.#pendingAutoOpen && !this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
      this.#pendingAutoOpen = false;
      this.#pendingSourceModal = null;
      requestAnimationFrame(() => this.#updateStickyState());
    };

    if (this.#pendingSourceModal?.open) {
      this.#pendingSourceModal.addEventListener('close', openAndSettle, { once: true });
    } else {
      openAndSettle();
    }
  };

  /**
   * Prevents a manual cart-icon click from opening stale/empty markup while an
   * add-to-cart section render is still in flight. Normal ready clicks continue
   * through Horizon's existing #cart-drawer/toggle handler unchanged.
   * @param {MouseEvent} event
   */
  #handleCartTriggerClick = async (event) => {
    const target = event.target instanceof Element ? event.target.closest('[aria-controls="cart-drawer"]') : null;
    if (!(target instanceof HTMLButtonElement) || this.#themeDrawer?.isOpen) return;
    if (this.#contentReady && this.#hasRenderableContent()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    target.setAttribute('aria-busy', 'true');

    try {
      if (!this.#contentReady) await this.#contentReadyPromise;

      if (!this.#hasRenderableContent()) {
        // The server-rendered drawer is genuinely stale/missing. Show an honest
        // loading state while the existing Section Rendering API refreshes it.
        this.#setLoading(true);
        this.#themeDrawer?.open();
        await this.#refreshDrawerContent();
      }

      if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
      requestAnimationFrame(() => this.#updateStickyState());
    } catch (error) {
      console.warn('[cart-drawer] Could not refresh drawer content before opening:', error);
    } finally {
      this.#setLoading(false);
      target.removeAttribute('aria-busy');
    }
  };

  #markContentPending() {
    if (!this.#contentReady) return;
    this.#contentReady = false;
    this.#contentReadyPromise = new Promise((resolve) => {
      this.#resolveContentReady = resolve;
    });
  }

  #markContentReady() {
    this.#contentReady = true;
    this.#resolveContentReady?.();
    this.#resolveContentReady = null;
    this.#contentReadyPromise = Promise.resolve();
  }

  #hasRenderableContent() {
    const inner = this.querySelector('.cart-drawer__inner');
    const cartItems = this.querySelector('cart-items-component');
    if (!inner || !cartItems) return false;

    const icon = /** @type {{ currentCartCount?: number } | null} */ (document.querySelector('cart-icon'));
    const expectedCount = Number(icon?.currentCartCount);
    const hasCartForm = this.querySelector('.cart-form') !== null;
    const isRenderedEmpty = inner.classList.contains('cart-drawer--empty');

    if (Number.isFinite(expectedCount)) {
      return expectedCount > 0 ? hasCartForm && !isRenderedEmpty : isRenderedEmpty && !hasCartForm;
    }

    return hasCartForm || isRenderedEmpty;
  }

  async #refreshDrawerContent() {
    const cartItems = /** @type {HTMLElement | null} */ (this.querySelector('cart-items-component'));
    const sectionId = cartItems?.dataset.sectionId;
    if (!sectionId) throw new Error('Cart drawer section id is unavailable');

    await sectionRenderer.renderSection(sectionId, {
      cache: false,
      mode: 'hydration',
      injectStylesheet: this.querySelector('[data-cart-drawer-empty]') !== null,
    });
    await customElements.whenDefined('cart-items-component');
    await Promise.resolve();
    this.#markContentReady();
  }

  /** @param {boolean} loading */
  #setLoading(loading) {
    const loadingState = /** @type {HTMLElement | null} */ (this.querySelector('[data-yomia-cart-loading]'));
    loadingState?.toggleAttribute('hidden', !loading);
    this.#dialog?.toggleAttribute('data-yomia-loading', loading);
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
